import React, { useMemo } from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';
import Svg, { Defs, LinearGradient, Rect, Stop, Text as SvgText } from 'react-native-svg';

import { ProfileImage } from '../../../assets';
// The crown, level badge and status dots are PNGs rather than inlined SVGs. On web every copy of
// an inlined SVG carries the same gradient and filter ids, and `url(#id)` resolves to the first
// copy in the document - on Social that was often the hidden Rankings screen's crown, so every
// crown here painted its outline only. Bitmaps have no ids to collide, and twenty of them are far
// cheaper to draw than twenty 60 KB vector crowns.
import CrownPng from '../../../assets/social/crown.png';
import LevelBadgePng from '../../../assets/social/level-badge.png';
import StatusDotOfflinePng from '../../../assets/social/status-dot-offline.png';
import StatusDotOnlinePng from '../../../assets/social/status-dot-online.png';
import type { PlayersDirectoryItem } from '../../../services/api';
import { getAvatarUrl } from '../../../utils/axiosInstance';
import { useSvgId } from '../../../utils/hooks/useSvgId';
import { Pressable } from '../common/Touchable';
import { SOCIAL_FONT, socialColors, socialGlow } from './socialTheme';

// Figma "Player Card" (1895:584) as the Social grid places it (2111:24795): 86x100. The extra
// height over the old 92 is what lets the VIP crown sit inside the card, 5 under its top edge.
export const FRIEND_CARD_BASE_WIDTH = 86;
export const FRIEND_CARD_BASE_HEIGHT = 100;

const PLATE_STROKE = 1;

type FriendCardProps = {
	friend: PlayersDirectoryItem;
	width?: number;
	onPress?: (friend: PlayersDirectoryItem) => void;
};

const FriendCard = ({ friend, width = FRIEND_CARD_BASE_WIDTH, onPress }: FriendCardProps) => {
	const scale = width / FRIEND_CARD_BASE_WIDTH;
	const plateId = useSvgId('friendCardPlate');

	const geometry = useMemo(() => {
		const s = (value: number) => value * scale;
		const height = s(FRIEND_CARD_BASE_HEIGHT);
		const plateRadius = s(12);

		return {
			height,
			plateRadius,
			card: { width, height, borderRadius: plateRadius },
			plateRect: {
				x: PLATE_STROKE / 2,
				y: PLATE_STROKE / 2,
				width: width - PLATE_STROKE,
				height: height - PLATE_STROKE,
				rx: plateRadius - PLATE_STROKE / 2,
			},
			avatarWell: {
				top: s(12),
				left: s(15.75),
				width: s(54),
				height: s(54),
				borderRadius: s(27),
				borderWidth: s(2),
			},
			crown: { top: s(5), left: s(34), width: s(17), height: s(15) },
			// The art is a 20 badge inside its 30 glow, so it sits 5 up and left of the design's 8/52.
			levelBadge: { top: s(47), left: s(3), size: s(30) },
			statusOnline: { top: s(47), left: s(59.5), size: s(24) },
			statusOffline: { top: s(53), left: s(65.5), size: s(12) },
			name: {
				top: s(75),
				left: s(4),
				width: s(77.5),
				fontSize: s(11),
				lineHeight: s(13.2),
			},
		};
	}, [scale, width]);

	return (
		<Pressable style={[styles.card, geometry.card]} onPress={() => onPress?.(friend)}>
			<Svg width={width} height={geometry.height} style={styles.plate}>
				<Defs>
					<LinearGradient id={plateId} x1='0' y1='0' x2='0' y2='1'>
						<Stop offset='0' stopColor={socialColors.cardGradientTop} />
						<Stop offset='0.801' stopColor={socialColors.cardGradientBottom} />
						<Stop offset='1' stopColor={socialColors.cardGradientBottom} />
					</LinearGradient>
				</Defs>
				<Rect
					{...geometry.plateRect}
					fill={`url(#${plateId})`}
					stroke={socialColors.borderOrange}
					strokeWidth={PLATE_STROKE}
				/>
			</Svg>

			<View style={[styles.avatarWell, geometry.avatarWell]}>
				<Image
					source={friend.avatarFileId ? { uri: getAvatarUrl(friend.avatarFileId) } : ProfileImage}
					style={styles.avatar}
					resizeMode='cover'
				/>
			</View>

			{friend.isVip && (
				<Image source={CrownPng} resizeMode='contain' style={[styles.crown, geometry.crown]} />
			)}

			<Image
				source={LevelBadgePng}
				style={[
					styles.absoluteArt,
					{
						top: geometry.levelBadge.top,
						left: geometry.levelBadge.left,
						width: geometry.levelBadge.size,
						height: geometry.levelBadge.size,
					},
				]}
			/>
			<Svg
				width={geometry.levelBadge.size}
				height={geometry.levelBadge.size}
				viewBox='0 0 30 30'
				style={[
					styles.levelTextArt,
					{ top: geometry.levelBadge.top, left: geometry.levelBadge.left },
				]}
			>
				<SvgText
					x='15'
					y='15'
					fill={socialColors.levelText}
					fontFamily={SOCIAL_FONT}
					fontSize='10'
					fontWeight='900'
					textAnchor='middle'
					alignmentBaseline='middle'
				>
					{friend.level}
				</SvgText>
			</Svg>

			{friend.isOnline ? (
				<Image
					source={StatusDotOnlinePng}
					style={[
						styles.absoluteArt,
						{
							top: geometry.statusOnline.top,
							left: geometry.statusOnline.left,
							width: geometry.statusOnline.size,
							height: geometry.statusOnline.size,
						},
					]}
				/>
			) : (
				<Image
					source={StatusDotOfflinePng}
					style={[
						styles.absoluteArt,
						{
							top: geometry.statusOffline.top,
							left: geometry.statusOffline.left,
							width: geometry.statusOffline.size,
							height: geometry.statusOffline.size,
						},
					]}
				/>
			)}

			<Text numberOfLines={1} ellipsizeMode='tail' style={[styles.name, geometry.name]}>
				{friend.username ?? '-'}
			</Text>
		</Pressable>
	);
};

const styles = StyleSheet.create({
	card: {
		backgroundColor: socialColors.cardGradientBottom,
		...socialGlow.card,
	},
	plate: {
		position: 'absolute',
		top: 0,
		left: 0,
	},
	avatarWell: {
		position: 'absolute',
		borderColor: socialColors.borderYellow,
		backgroundColor: socialColors.avatarWell,
		overflow: 'hidden',
		zIndex: 2,
	},
	avatar: {
		width: '100%',
		height: '100%',
	},
	crown: {
		position: 'absolute',
		zIndex: 3,
	},
	absoluteArt: {
		position: 'absolute',
		zIndex: 3,
	},
	levelTextArt: {
		position: 'absolute',
		zIndex: 4,
	},
	name: {
		position: 'absolute',
		textAlign: 'center',
		color: socialColors.name,
		fontFamily: SOCIAL_FONT,
		fontWeight: '500',
		zIndex: 2,
	},
});

// Memoised: each card draws two SVGs, and a page loaded while scrolling must not redraw the cards
// already on screen. The grid passes the same `friend` objects and a stable `onPress`.
export default React.memo(FriendCard);
